import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { useState } from "react";

import { formatContainerDateLong } from "../Containers/containerDisplay";
import { ENTERED_BEFORE_CONTAINERS, enteredNotAppliedNote, formatPounds } from "./episodicDisplay";
import {
  calculatedOf,
  DeclarationFields,
  declarationOf,
  EventStreamRow,
  parseStreamRow,
} from "./eventManifestRows";
import { ExemptManifest } from "./manifestExemptionForm";
import { ConfirmTarget, removeConfirmFor, useRemoveExemption } from "./removeExemption";

const READ_ONLY_COPY = `${ENTERED_BEFORE_CONTAINERS} Where they apply, they replace the container weight in generator status. You can remove them; to change the amount, update the containers instead.`;

type DeclaredRow = { row: EventStreamRow; declaration: DeclarationFields };

function declaredRows(manifest: ExemptManifest): DeclaredRow[] {
  return manifest.streams.flatMap((stream) => {
    const row = parseStreamRow(manifest, stream);
    const declaration = row && declarationOf(row.basis);
    return row && declaration ? [{ row, declaration }] : [];
  });
}

function StreamRow({
  row,
  declaration,
  onRemove,
}: DeclaredRow & { onRemove: (() => void) | null }) {
  const calculated = calculatedOf(row.basis);
  const notApplied = enteredNotAppliedNote(row.state);
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{row.wasteStream.name}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Entered by {declaration.updatedBy} on {formatContainerDateLong(declaration.updatedAt)}
        </Typography>
        {notApplied && (
          <Typography variant="caption" color="text.secondary" display="block">
            {notApplied}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {calculated ? (
          <Box component="s" sx={{ color: "text.secondary" }}>
            {formatPounds(calculated.pounds)}
          </Box>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell align="right">{formatPounds(declaration.declaredPounds)}</TableCell>
      {onRemove && (
        <TableCell align="right">
          <Button size="small" color="error" onClick={onRemove}>
            Remove
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
}

/**
 * A declaration on an event whose pounds now come from its containers: shown
 * against the container weight it replaces, removable but not editable.
 */
export function DeclarationReviewDialog({
  eventReason,
  manifest,
  extraRefetchQueries,
  onClose,
}: {
  eventReason: string;
  manifest: ExemptManifest;
  extraRefetchQueries?: string[];
  onClose: () => void;
}) {
  const [confirm, setConfirm] = useState<ConfirmTarget | null>(null);
  const { remove, removing } = useRemoveExemption(extraRefetchQueries);
  const rows = declaredRows(manifest);
  const single = rows.length === 1 ? rows[0] : null;
  const removeSingle = single ? removeConfirmFor(single.row) : null;

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        title={`Episodic pounds on ${manifest.manifestTrackingNumber}`}
        noPrimaryAction
        additionalPrimaryActionElements={[
          ...(removeSingle
            ? [
                <Button key="remove" color="error" onClick={() => setConfirm(removeSingle)}>
                  Remove declaration
                </Button>,
              ]
            : []),
          <Button key="close" variant="contained" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Event
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" fontWeight={600}>
                {eventReason}
              </Typography>
              <Chip size="small" label="Tracks containers" />
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {READ_ONLY_COPY}
          </Typography>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pounds are entered on this manifest.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Waste stream</TableCell>
                  <TableCell align="right">Container weight</TableCell>
                  <TableCell align="right">Entered · applied</TableCell>
                  {!single && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((declared) => (
                  <StreamRow
                    key={declared.row.wasteStream.id}
                    {...declared}
                    onRemove={single ? null : () => setConfirm(removeConfirmFor(declared.row))}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </Dialog>
      <ConfirmDialog
        open={confirm != null}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm) {
            void remove(confirm, (removed) => {
              setConfirm(null);
              if (removed && single) {
                onClose();
              }
            });
          }
        }}
        loading={removing}
        header={confirm?.header ?? ""}
        info={confirm?.info ?? ""}
        confirmText={confirm?.confirmText ?? "Remove"}
      />
    </>
  );
}
